import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    MdAdd,
    MdVisibility,
    MdEdit,
    MdDelete,
    MdMoreVert,
    MdSearch
} from 'react-icons/md';
import './Projects.css';
import '../pages/Dashboard.css'; // Reuse dashboard table styles
import Swal from 'sweetalert2';
import axios from 'axios';
import { GET_ALL_PROJECT_API, DELETE_PROJECT_API } from '../config/API';
import { formatDate } from '../utils/dateUtils';
import Loader from '../components/common/Loader';
import NoData from '../components/common/NoData';
import Pagination from '../components/common/Pagination';
import ActionMenuPortal from '../components/common/ActionMenuPortal';
import { useUser } from '../context/UserContext';

const Projects = () => {
    const navigate = useNavigate();
    const { hasPermission } = useUser();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch]);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GET_ALL_PROJECT_API, {
                params: {
                    page: currentPage,
                    limit: 10,
                    search: debouncedSearch || undefined
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                setProjects(response.data.data || []);
                const pagination = response.data.pagination || {};
                setTotalPages(pagination.totalPages || response.data.totalPages || 1);
                setTotalRecords(pagination.total || pagination.totalRecords || response.data.totalRecords || 0);
            }
        } catch (error) {
            console.error('Error fetching projects:', error);
            // Swal.fire('Error', 'Failed to fetch projects', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProjects();
    }, [currentPage, debouncedSearch]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const [openMenuId, setOpenMenuId] = useState(null);
    const [anchorRect, setAnchorRect] = useState(null);


    const toggleMenu = (id, e) => {
        e.stopPropagation();
        if (openMenuId === id) {
            setOpenMenuId(null);
            setAnchorRect(null);
        } else {
            setOpenMenuId(id);
            setAnchorRect(e.currentTarget.getBoundingClientRect());
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest('.swal2-container')) return;
            if (event.target.closest('.action-portal-wrapper')) return;
            if (event.target.closest('.action-menu-btn')) return;

            setOpenMenuId(null);
            setAnchorRect(null);
        };

        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Project?',
            text: 'Are you sure you want to delete this project?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc2626',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-delete-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('accessToken');
                const response = await axios.delete(DELETE_PROJECT_API(id), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire('Deleted!', 'Project has been deleted.', 'success');

                    // Optimistic update
                    setProjects(prev => prev.filter(project => (project.id || project._id) !== id));

                    // If it was the last item on the page and not on page 1, go to previous page
                    if (projects.length === 1 && currentPage > 1) {
                        setCurrentPage(prev => prev - 1);
                    } else {
                        fetchProjects();
                    }
                } else {
                    Swal.fire('Error', response.data.error || response.data.message || 'Failed to delete project', 'error');
                }
            } catch (error) {
                console.error('Error deleting project:', error);
                Swal.fire('Error', error.response?.data?.error || error.response?.data?.message || 'An error occurred while deleting', 'error');
            }
        }
    };

    return (
        <div className="dashboard"> {/* Using dashboard class for consistent padding/layout */}
            <div className="projects-header">
                <h2 className="section-title" style={{ margin: 0 }}>Projects</h2>
                {hasPermission('PROJECTS', 'create') && (
                    <Link to="/projects/new" className="add-project-btn">
                        <MdAdd size={20} />
                        Add Project
                    </Link>
                )}
            </div>

            <div className="card-container-projects">
                {/* Search Bar */}
                <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                    <div style={{ position: 'relative', maxWidth: '500px' }}>
                        <MdSearch
                            style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#9ca3af',
                                fontSize: '1.25rem'
                            }}
                        />
                        <input
                            type="text"
                            placeholder="Search projects..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.625rem 1rem 0.625rem 2.5rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                                background: 'white'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
                            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: '#e5e7eb',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    color: '#4b5563',
                                    fontSize: '12px'
                                }}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>
                {loading ? (
                    <Loader text="Fetching projects..." />
                ) : !projects || projects.length === 0 ? (
                    <NoData
                        title="No Projects Found"
                        message="Get started by creating your first project."
                        onAction={() => navigate('/projects/new')}
                        actionText="Add Project"
                    />
                ) : (
                    <>
                        <div className="table-container">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Project Name</th>
                                        <th>Location</th>
                                        <th>Start Date</th>
                                        <th>End Date</th>
                                        <th>Status</th>
                                        <th style={{ textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {projects.map((project, index) => (
                                        <tr key={project.id || project._id}>
                                            <td>
                                                <strong>{project.projectName}</strong>
                                                {/* <div className="client-info">
                                                    <span>{project.Description || 'No description'}</span>
                                                </div> */}
                                            </td>
                                            <td>{project.projectAdd || '-'}</td>
                                            <td>{formatDate(project.projectStartDate)}</td>
                                            <td>{formatDate(project.projectEndDate)}</td>
                                            <td>
                                                <span className={`status-badge ${project.status === 'Active' ? 'status-normal' : 'status-success'}`}
                                                    style={project.status === 'Completed' ? { backgroundColor: '#d1fae5', color: '#065f46' } : {}}>
                                                    {project.status}
                                                </span>
                                            </td>
                                            <td className={`action-cell ${openMenuId === project.id ? 'menu-open' : ''}`}>
                                                <button
                                                    className="action-menu-btn"
                                                    onClick={(e) => toggleMenu(project.id, e)}
                                                    title="Actions"
                                                >
                                                    <MdMoreVert size={20} />
                                                </button>

                                                <ActionMenuPortal
                                                    isOpen={openMenuId === project.id}
                                                    anchorRect={anchorRect}
                                                    onClose={() => setOpenMenuId(null)}
                                                >
                                                    <div className="action-dropdown">
                                                        {hasPermission('PROJECTS', 'read') && (
                                                            <button className="action-item" onClick={() => navigate(`/projects/view/${project.id}`)}>
                                                                <MdVisibility size={16} /> View Details
                                                            </button>
                                                        )}
                                                        {hasPermission('PROJECTS', 'update') && (
                                                            <button className="action-item" onClick={() => navigate(`/projects/edit/${project.id}`)}>
                                                                <MdEdit size={16} /> Edit Project
                                                            </button>
                                                        )}
                                                        {hasPermission('PROJECTS', 'delete') && (
                                                            <button
                                                                className="action-item delete"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDelete(project.id);
                                                                }}
                                                            >
                                                                <MdDelete size={16} /> Delete
                                                            </button>
                                                        )}
                                                    </div>
                                                </ActionMenuPortal>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="mobile-card-view">
                            {projects.map((project) => (
                                <div key={project.id || project._id} className="project-card">
                                    <div className="card-header">
                                        <div className="card-title">
                                            <strong>{project.projectName}</strong>
                                            <span className={`status-badge ${project.status === 'Active' ? 'status-normal' : 'status-success'}`}
                                                style={project.status === 'Completed' ? { backgroundColor: '#d1fae5', color: '#065f46' } : {}}>
                                                {project.status}
                                            </span>
                                        </div>
                                        <button
                                            className="action-menu-btn"
                                            onClick={(e) => toggleMenu(project.id || project._id, e)}
                                            title="Actions"
                                        >
                                            <MdMoreVert size={20} />
                                        </button>
                                        <ActionMenuPortal
                                            isOpen={openMenuId === (project.id || project._id)}
                                            anchorRect={anchorRect}
                                            onClose={() => setOpenMenuId(null)}
                                        >
                                            <div className="action-dropdown shadow-lg">
                                                {hasPermission('PROJECTS', 'read') && (
                                                    <button className="action-item" onClick={() => navigate(`/projects/view/${project.id || project._id}`)}>
                                                        <MdVisibility size={16} /> View Details
                                                    </button>
                                                )}
                                                {hasPermission('PROJECTS', 'update') && (
                                                    <button className="action-item" onClick={() => navigate(`/projects/edit/${project.id || project._id}`)}>
                                                        <MdEdit size={16} /> Edit Project
                                                    </button>
                                                )}
                                                {hasPermission('PROJECTS', 'delete') && (
                                                    <button
                                                        className="action-item delete"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(project.id || project._id);
                                                        }}
                                                    >
                                                        <MdDelete size={16} /> Delete
                                                    </button>
                                                )}
                                            </div>
                                        </ActionMenuPortal>
                                    </div>
                                    <div className="card-body">
                                        <div className="card-row">
                                            <span className="card-label">Description:</span>
                                            <span className="card-value">{project.Description}</span>
                                        </div>
                                        <div className="card-row">
                                            <span className="card-label">Location:</span>
                                            <span className="card-value">{project.projectAdd}</span>
                                        </div>
                                        <div className="card-row">
                                            <span className="card-label">Start Date:</span>
                                            <span className="card-value">{formatDate(project.projectStartDate)}</span>
                                        </div>
                                        <div className="card-row">
                                            <span className="card-label">End Date:</span>
                                            <span className="card-value">{formatDate(project.projectEndDate)}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            totalRecords={totalRecords}
                            currentCount={projects.length}
                            entityName="Projects"
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default Projects;
